import { getRequiredJsonObject, UPLOAD_BUCKET, withS3Client, type S3Io } from './s3'
import { appendToyToIndex, type ToyIndexEntry } from './toyIndex'
import {
  getVersionsIndex,
  listVersionFolders,
  putVersionsIndex,
  type VersionsIndex,
} from './toyVersions'

const METADATA_FILENAME = 'metadata.json'

// Promotes a version folder to be the toy's live version. "Live" is entirely
// determined by versions-index.json latest plus the toy-index entry, so this
// is two writes — no file copying. Promoting an older version back works the
// same way, so this is also the rollback path.

export async function promoteToyVersion(
  io: S3Io,
  identityId: string,
  toyId: string,
  version: string,
): Promise<void> {
  const metadata = await withS3Client(io, async (client) => {
    // Validate the target before writing anything: its metadata must exist
    // (the per-version completion marker) and must actually describe this
    // version — a mismatch would publish a manifestUrl pointing at the wrong
    // folder, breaking installs.
    const meta = await getRequiredJsonObject(
      client,
      UPLOAD_BUCKET,
      `${identityId}/${toyId}/${version}/${METADATA_FILENAME}`,
      METADATA_FILENAME,
    )
    if (meta.version !== version) {
      throw new Error(
        `metadata.json says version ${JSON.stringify(meta.version)} but the folder is ${version}`,
      )
    }
    const manifestUrl = meta.manifestUrl
    // The URL must name this folder's copy of this version — identity
    // included. Blind-copied featured versions can still carry the source
    // user's URL; publishing one would serve installs from another user's
    // folder, so refuse loudly (delete + re-copy is the remedy).
    if (
      typeof manifestUrl === 'string' &&
      manifestUrl.includes(`/${toyId}/`) &&
      !manifestUrl.includes(`/${identityId}/${toyId}/${version}/`)
    ) {
      throw new Error(
        `metadata.json manifestUrl does not point at this folder's ${version}/: ${manifestUrl}`,
      )
    }

    // Flip versions-index latest (reconstructing a missing/corrupt index from
    // the folders on disk, like regenerate's self-heal). Unconditional put —
    // same single-admin-writer caveat as the rest of the admin tools.
    const existing = await getVersionsIndex(client, identityId, toyId)
    const index: VersionsIndex = existing ?? {
      schemaVersion: 1,
      latest: version,
      versions: (await listVersionFolders(client, identityId, toyId)).map((v) => ({
        version: v,
      })),
    }
    index.latest = version
    if (!index.versions.some((v) => v.version === version)) {
      index.versions.push({
        version,
        createdAt:
          typeof meta.creationTime === 'string' ? meta.creationTime : new Date().toISOString(),
      })
    }
    await putVersionsIndex(client, identityId, toyId, index)
    return meta
  })

  // Refresh the catalog entry so the front page / install link serve the
  // promoted version. appendToyToIndex replaces the same-id entry with a
  // conditional write and falls back to a disk rebuild on drift, and that
  // rebuild reads the latest via the versions-index we just wrote.
  await appendToyToIndex(io, identityId, metadata as ToyIndexEntry)
}
